import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Step,
  StepIconProps,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { selectUserInfo } from "features/user/userSlice";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  BusinessProfileService,
  UserModuleProfileService,
} from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { ReactComponent as Business } from "../../assets/SideBarIcons/business.svg";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useRef, useState } from "react";
import CommonCheckbox from "common/CheckBox/Checkbox";
import DynamicSelect from "common/Select/Select";
import Currency from "common/Others/Currency";
import Language from "common/Others/Language";
import { useGetItemGroupDropdownService } from "subModule/src/services/itemgroup/useItemGroup";
import { useGetPriceListDropdownService } from "subModule/src/services/pricelist/usePriceList";
import { useGetTaxProcedureDropdownService } from "subModule/src/services/taxProcedure/useTaxProcedure";
import Loader from "layout/Loader";
import BusinessMedia from "./BusinessMedia";
import { themes } from "theme/themes";
import { fetchMediaAsDataUrl } from "Helpers/mediaView";
import MediaImage from "common/MediaImage/MediaImage";
import { brandingInfo } from "features/server/brandingSlice";
import VerticalTabs from "common/ComponentSidebar/ComponentSideBar";
import { useGetBankAccountsDropdownService } from "subModule/src/services/bankaccount/useBankAccount";
import { useGetVendorService } from "subModule/src/services/vendor/useVendor";
import { useGetSubscriptionPlanDropdownService } from "subModule/src/services/subscriptionplans/useSubscriptionPlan";
import { useGetRoleDropdownService } from "subModule/src/services/role/useRole";
import StationModal from "pages/Station/StationModal";
// import StationSubscriptionPlanStep from "./StationSubscriptionPlanStep";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
const tabs = [
  { id: 0, title: `Details`, child: <></> },
  { id: 1, title: `branding`, child: <></> },
  { id: 2, title: `themeSettings`, child: <></> },
  { id: 3, title: `integrations`, child: <></> },
  { id: 4, title: `stations`, child: <></> },
  // { id: 5, title: `subscriptionPlanUsage`, child: <></> },
];
const CREATE_WIZARD_STEPS = [
  "businessDetails",
  "stationDetails",
  // "subscriptionPlan",
];

const WizardStepIcon = ({
  active,
  completed,
  icon,
  activeColor,
}: StepIconProps & { activeColor: string }) => {
  const isFilled = Boolean(active || completed);
  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        backgroundColor: isFilled ? activeColor : "transparent",
        border: isFilled ? "none" : `2px solid ${activeColor}40`,
        color: isFilled ? "#fff" : `${activeColor}99`,
        fontFamily: "Saira, sans-serif",
        fontWeight: 700,
        fontSize: "13px",
        lineHeight: 1,
      }}
    >
      {completed ? <CheckIcon sx={{ fontSize: 16 }} /> : icon}
    </Box>
  );
};

const extractFileNameFromUrl = (url: string): string => {
  if (!url) return "";
  try {
    return new URL(url).pathname.split("/").pop() || "";
  } catch {
    return url.split("?")[0].split("/").pop() || "";
  }
};

const mapBusinessFormToPayload = (data: any, businessId?: number) => ({
  ...(businessId ? { id: businessId } : {}),
  title: data.title,
  secondaryTitle: data.secondaryTitle,
  primaryLanguage: data.primaryLanguage?.value || "",
  secondaryLanguage: data.secondaryLanguage?.value || "",
  currency: data.currency?.value || "",
  whiteLabel: !!data.whiteLabel,
  cashContractNo: data.cashContractNo || null,
});

const buildMaintenanceHeadUserPayload = (data: any, businessId?: number) => ({
  name: data.name,
  secondaryName: data.secondaryName,
  username: data.username,
  email: data.username,
  phone: data.phone,
  address: data.address,
  taxNumber: data.taxNumber,
  roleId: data?.roleId?.value || 0,
  type: 110,
  business: mapBusinessFormToPayload(data, businessId),
});

export default function BusinessModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [phoneLabel, setPhoneLabel] = useState<string>("phoneNumber");
  const [maintenanceHeadUserId, setMaintenanceHeadUserId] = useState<
    number | null
  >(null);
  const [wizardStep, setWizardStep] = useState<0 | 1>(0);
  const [savedBusinessId, setSavedBusinessId] = useState<number | null>(null);
  const isCreateWizard = !params?.id;
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    reset,
    watch,
    getValues,
    setValue,
  } = useForm<FormData>({
    defaultValues: {},
  });
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const isArabicLanguage = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    return l === "arabic" || l === "ar" || l.includes("arab");
  };
  const localizedBusinessTitlePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phBusinessTitleAr") : t("phBusinessTitleEn");
  const localizedUserNamePlaceholder = (lang?: string) =>
    isArabicLanguage(lang) ? t("phUserNameAr") : t("phUserNameEn");
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "businessDetails",
    moduleHref: "/businesses",
    entityFallbackLabelKey: "title",
    isEdit: !!params?.id,
  });
  const [mediaPreviews, setMediaPreviews] = useState<{
    logo: string;
    logInPageImage: string;
    favIcon: string;
  }>({ logo: "", logInPageImage: "", favIcon: "" });
  const { data: scrapItemGroupsData, refetch: scrapItemGroupsRefetch } =
    useGetItemGroupDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
      businessId: params?.id,
    });
  const { data: priceListData, refetch: priceListRefetch } =
    useGetPriceListDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
      businessId: params?.id,
    });
  const { refetch: taxProceduresRefetch } = useGetTaxProcedureDropdownService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
    businessId: params?.id,
  });
  const { refetch: bankAccountsRefetch } = useGetBankAccountsDropdownService({
    pageNumber: pageNo,
    size: 50,
    search: dropdownSearchTerm,
    businessId: params?.id,
  });
  const { refetch: vendorsRefetch } = useGetVendorService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
    businessId: params?.id,
  });
  const { refetch: subscriptionPlansRefetch } =
    useGetSubscriptionPlanDropdownService({
      pageNumber: pageNo,
      size: 50,
      title: dropdownSearchTerm,
    });
  const { data: rolesData, refetch: rolesRefetch } = useGetRoleDropdownService({
    pageNumber: pageNo,
    size: 50,
    title: dropdownSearchTerm,
  });
  /*
  const scrapItemGroups = (
    scrapItemGroupsData?.pages[0]?.data?.content || []
  ).map((item: { id: number; title: string }) => ({
    value: item.id,
    label: item.title,
  }));
  const priceLists = (priceListData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const taxProcedures = (taxProceduresData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const supplierOptions = (vendorsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const bankAccounts = (bankAccountsData?.pages[0]?.data?.content || []).map(
    (item: { id: number; accountTitle: string }) => ({
      value: item.id,
      label: item.accountTitle,
    }),
  );
  const subscriptionPlans = (
    subscriptionPlansData?.pages[0]?.data?.content || []
  ).map((item: { id?: number | undefined; name?: string }) => ({
    value: item.id,
    label: item.name,
  }));
  */
  const roles = (rolesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string }) => ({
      value: item.id,
      label: item.title,
    }),
  );
  const currencyOptions = Currency.map(
    (item: { value: string; title: string }) => ({
      value: item.value,
      label: item.title,
    }),
  );
  const languageOptions = Language.map(
    (item: { value: string; title: string }) => ({
      value: item.value,
      label: item.title,
    }),
  );
  const isScreenSmallOrGreater = useMediaQuery("(min-width:600px)");
  useEffect(() => {
    scrapItemGroupsRefetch();
    priceListRefetch();
    taxProceduresRefetch();
    vendorsRefetch();
    bankAccountsRefetch();
    subscriptionPlansRefetch();
    rolesRefetch();
  }, []);
  useEffect(() => {
    handleChangeTabs(0);
  }, []);
  useEffect(() => {
    if (loggedInUserData.type === -1 && watch("whiteLabel") && params?.id) {
      setIsBtnEnabled(true);
    }
  }, [currentTab]);
  useEffect(() => {
    if (params?.id && scrapItemGroupsData && priceListData) {
      if (loading) getByID();
    }
  }, [params, scrapItemGroupsData, priceListData]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  useEffect(() => {
    if (!params?.id) return;
    const subscription = watch((_values, info) => {
      if (info?.type === "change") setIsBtnEnabled(true);
    });
    return () => subscription.unsubscribe();
  }, [params?.id, watch]);
  const enableUpdateBtn = () => {
    if (params?.id) setIsBtnEnabled(true);
  };
  const isBrandingEdit =
    params?.id && loggedInUserData.type === -1 && watch("whiteLabel");
  const showCreateWizardButtons = isCreateWizard && wizardStep === 0;
  const mainButtonText = showCreateWizardButtons
    ? "saveAndNext"
    : params?.id
      ? isBrandingEdit && currentTab !== tabs.length - 2
        ? "Next"
        : "update"
      : "submit";
  const secondaryButtonText = showCreateWizardButtons
    ? "saveAndClose"
    : isBrandingEdit
      ? mainButtonText === "Next"
        ? "update"
        : "Next"
      : undefined;
  const handleSubmitAuto = handleSubmit((data) => onSubmit(data));
  const handleSubmitSave = handleSubmit((data) => onSubmit(data, "save"));
  const handleSubmitSaveAndNext = handleSubmit((data) =>
    onSubmit(data, "saveAndNext"),
  );
  const handleSubmitUpdate = handleSubmit((data) => onSubmit(data, "update"));
  const handleSubmitNext = handleSubmit((data) => onSubmit(data, "next"));
  const secondaryButtonAction = showCreateWizardButtons
    ? handleSubmitSave
    : isBrandingEdit
      ? mainButtonText === "Next"
        ? handleSubmitUpdate
        : handleSubmitNext
      : undefined;
  const activeFormSubmit = showCreateWizardButtons
    ? handleSubmitSaveAndNext
    : handleSubmitAuto;
  const handleChangeTabs = (tab: number) => {
    if (tab === 3) navigate(`/business/${params?.id}/integrations`);
    else if (tab === 4) navigate(`/business/${params?.id}/stations`);
    else if (tab === 5)
      navigate(`/business/${params?.id}/subscription-analytics`);
    else setCurrentTab(tab);
  };
  const getByID = () => {
    BusinessProfileService.getBusinessByID(+(params as any)?.id)
      .then(async (res) => {
        if (res.success) {
          const businessUser = (res?.data as any)?.user;
          if (businessUser?.id) {
            setMaintenanceHeadUserId(businessUser.id);
            setPhoneLabel("phoneNumber");
          }
          reset({
            ...(res?.data || {}),
            ...(businessUser
              ? {
                  name: businessUser.name,
                  secondaryName: businessUser.secondaryName,
                  username: businessUser.username,
                  phone: businessUser.phone,
                  address: businessUser.address,
                  taxNumber: businessUser.taxNumber,
                  type: businessUser.type ?? 110,
                  roleId: businessUser.roleId
                    ? {
                        value: businessUser.roleId,
                        label: businessUser.roleTitle,
                      }
                    : null,
                }
              : {}),
            primaryLanguage: res.data?.primaryLanguage
              ? {
                  value: res.data?.primaryLanguage,
                  label: res?.data?.primaryLanguage,
                }
              : null,
            secondaryLanguage: res.data?.secondaryLanguage
              ? {
                  value: res.data?.secondaryLanguage,
                  label: res?.data?.secondaryLanguage,
                }
              : null,
            currency: res.data?.currency
              ? {
                  value: res.data?.currency,
                  label:
                    currencyOptions.find((v) => v.value === res.data?.currency)
                      ?.label || res.data?.currency,
                }
              : null,
            taxProcedureId: res.data?.taxProcedureId
              ? {
                  value: res.data?.taxProcedureId,
                  label:
                    res.data?.taxProcedureTitle || res.data?.taxProcedureId,
                }
              : null,
            scrapItemGroupId: res.data?.scrapItemGroupId
              ? {
                  value: res.data?.scrapItemGroupId,
                  label:
                    res.data?.scrapItemGroupTitle || res.data?.scrapItemGroupId,
                }
              : null,
            priceListId: res.data?.priceListId
              ? {
                  value: res.data?.priceListId,
                  label: res.data?.priceListTitle || res.data?.priceListId,
                }
              : null,
            bankAccountId: res.data?.bankAccountId
              ? {
                  value: res.data?.bankAccountId,
                  label: res.data?.bankAccountTitle || res.data?.bankAccountId,
                }
              : null,
            vendorId: res.data?.vendorId
              ? {
                  value: res.data?.vendorId,
                  label: res.data?.vendorName || res.data?.vendorId,
                }
              : null,
            planId: res?.data?.businessPlan?.planId
              ? {
                  value: res?.data?.businessPlan?.planId,
                  label:
                    res?.data?.businessPlan?.planTitle ||
                    res?.data?.businessPlan?.planId,
                }
              : null,
            attributes: {
              ...(res?.data?.attributes || {}),
              customTheme:
                res?.data?.attributes?.themeId === 100
                  ? res?.data?.attributes?.customTheme
                  : null,
              themeId: res?.data?.attributes?.themeId
                ? {
                    ...(themes as any)[`${res.data?.attributes?.themeId}`],
                    value: res.data?.attributes?.themeId,
                    label: Object?.values(themes).find(
                      (v) => v.id === res.data?.attributes?.themeId,
                    )?.label,
                  }
                : { ...themes["3"], value: 3, label: t("Theme 3 (Default)") },
            },
          });
          const attrs = res?.data?.attributes || {};
          const [logo, logInPageImage, favIcon] = await Promise.all([
            fetchMediaAsDataUrl((attrs as any)?.logo || ""),
            fetchMediaAsDataUrl((attrs as any)?.logInPageImage || ""),
            fetchMediaAsDataUrl((attrs as any)?.favIcon || ""),
          ]);
          setMediaPreviews({ logo, logInPageImage, favIcon });
          if (
            currentTab !== tabs.length - 2 &&
            loggedInUserData.type === -1 &&
            res?.data?.whiteLabel
          ) {
            setIsBtnEnabled(true);
          }
          captureLabel(res?.data?.title);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const saveBusinessAndUser = (data: any, goToStationStep: boolean) => {
    delete data.showConfirmPassword;
    delete data.showPassword;
    delete data.confirmPassword;

    const handleSuccess = (businessId: number) => {
      setSavedBusinessId(businessId);
      enqueueSnackbar(
        <AlertMessages
          text={t("business") + " " + t("created") + " " + t("successfully")}
        />,
        { variant: "success" },
      );
      setIsBtnEnabled(true);
      if (goToStationStep) {
        setWizardStep(1);
      } else {
        navigate(-1);
      }
    };

    if (savedBusinessId && maintenanceHeadUserId) {
      Promise.all([
        BusinessProfileService.updateBusiness(
          savedBusinessId,
          mapBusinessFormToPayload(data, savedBusinessId) as any,
        ),
        UserModuleProfileService.updateUser(
          maintenanceHeadUserId,
          buildMaintenanceHeadUserPayload(data, savedBusinessId),
        ),
      ])
        .then(([businessRes, userRes]: [any, any]) => {
          if (businessRes?.success !== false && userRes?.success !== false) {
            handleSuccess(savedBusinessId);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(
              <AlertMessages
                text={t(businessRes?.message || userRes?.message)}
              />,
              { variant: "error" },
            );
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
      return;
    }

    UserModuleProfileService.createUser({
      ...buildMaintenanceHeadUserPayload(data),
      password: data.password,
      active: true,
    })
      .then((response: any) => {
        if (response.success) {
          const businessId =
            Number(response?.data?.business?.id) ||
            Number(response?.data?.businessId) ||
            Number(response?.data?.data?.business?.id) ||
            Number(response?.data?.data?.businessId);
          if (response?.data?.id) {
            setMaintenanceHeadUserId(response.data.id);
          }
          if (businessId) {
            handleSuccess(businessId);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(
              <AlertMessages text="Business id not returned from server" />,
              { variant: "error" },
            );
          }
        } else {
          setIsBtnEnabled(true);
          enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setIsBtnEnabled(true);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      });
  };

  const onSubmit = (
    data: any,
    action: "auto" | "update" | "next" | "save" | "saveAndNext" = "auto",
  ) => {
    // setIsBtnEnabled(false);

    const performUpdate = () => {
      if (!params?.id) {
        setIsBtnEnabled(true);
        return;
      }
      const businessId = Number(params.id);
      delete data.sellBelowStock;
      BusinessProfileService.updateBusiness(businessId, {
        ...data,
        primaryLanguage: data.primaryLanguage?.value || "",
        secondaryLanguage: data.secondaryLanguage?.value || "",
        currency: data.currency?.value || "",
        scrapItemGroupId: data.scrapItemGroupId?.value,
        priceListId: data.priceListId?.value,
        taxProcedureId: data.taxProcedureId?.value || 0,
        bankAccountId: data.bankAccountId?.value || null,
        vendorId: data.vendorId?.value || null,
        planId: data?.planId?.value || null,
        whiteLabel: !!data.whiteLabel,
        cashContractNo: data.cashContractNo || null,
        attributes: {
          ...(data?.attributes || {}),
          logo: extractFileNameFromUrl(data?.attributes?.logo || ""),
          logInPageImage: extractFileNameFromUrl(
            data?.attributes?.logInPageImage || "",
          ),
          favIcon: extractFileNameFromUrl(data?.attributes?.favIcon || ""),
          customTheme:
            data?.attributes?.themeId?.value === 100
              ? data?.attributes?.customTheme
              : null,
          themeId: data?.attributes?.themeId?.value || 3,
        },
      })
        .then((response: any) => {
          if (!response.success) {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
            // setIsBtnEnabled(true);
            return;
          }
          captureLabel(data?.title);
          const isBrandingOrThemeTab = currentTab === 1 || currentTab === 2;
          if (!maintenanceHeadUserId || isBrandingOrThemeTab) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("business") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            // window.location.href = "/businesses";
            return;
          }
          UserModuleProfileService.updateUser(
            maintenanceHeadUserId,
            buildMaintenanceHeadUserPayload(data, businessId),
          )
            .then((userResponse: any) => {
              if (userResponse.success) {
                enqueueSnackbar(
                  <AlertMessages
                    statusCode={userResponse.status}
                    text={
                      t("business") +
                      " " +
                      t("updated") +
                      " " +
                      t("successfully")
                    }
                  />,
                  { variant: "success" },
                );
                // window.location.href = "/businesses";
              } else {
                enqueueSnackbar(
                  <AlertMessages text={userResponse?.message} />,
                  { variant: "error" },
                );
              }
              // setIsBtnEnabled(true);
            })
            .catch((err: any) => {
              console.error("===", err);
              // setIsBtnEnabled(true);
              enqueueSnackbar(
                <AlertMessages
                  text={
                    err?.message || err?.data?.message || "Something went wrong"
                  }
                />,
                { variant: "error" },
              );
            });
        })
        .catch((err: any) => {
          console.error("===", err);
          // setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        });
    };

    if (params?.id) {
      if (action === "update") {
        performUpdate();
      } else if (action === "next") {
        if (
          loggedInUserData.type === -1 &&
          currentTab !== tabs.length - 2 &&
          watch("whiteLabel")
        ) {
          handleChangeTabs(currentTab + 1);
        } else {
          performUpdate();
        }
      } else {
        // auto (original behaviour)
        if (
          loggedInUserData.type === -1 &&
          currentTab !== tabs.length - 2 &&
          watch("whiteLabel")
        ) {
          handleChangeTabs(currentTab + 1);
        } else {
          performUpdate();
        }
      }
    } else if (
      action === "save" ||
      action === "saveAndNext" ||
      action === "auto"
    ) {
      saveBusinessAndUser(data, action === "saveAndNext");
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setPageNo(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setPageNo(pageNo + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "taxProcedures")
        taxProceduresRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "scrapItemGroups")
        scrapItemGroupsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "priceLists")
        priceListRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "accounts")
        bankAccountsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "suppliers")
        vendorsRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "subscriptionPlans")
        subscriptionPlansRefetch().finally(() => setSearchLoading(null));
      else if (typeName === "roles")
        rolesRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={params?.id ? false : wizardStep === 0}
        onClick={() => {
          // if (isCreateWizard && wizardStep === 2) {
          //   setWizardStep(1);
          //   return;
          // }
          if (isCreateWizard && wizardStep === 1) {
            setWizardStep(0);
            return;
          }
          navigate(-1);
        }}
        onSubmit={activeFormSubmit}
        heading={
          // isCreateWizard && wizardStep === 2
          //   ? t("subscriptionPlan")
          //   :
          isCreateWizard && wizardStep === 1
            ? t("addStations")
            : params?.id
              ? t("editBusiness")
              : t("addBusiness")
        }
        icon={<Business height={28} width={28} />}
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={checkPrivileges("ROLE_BUSINESS_CREATE")}
        breadcrumbItems={breadcrumbItems}
      />
      {isCreateWizard ? (
        <Box sx={{ px: 0, pb: 2, m: 0, width: "65%" }}>
          <Stepper
            activeStep={wizardStep}
            className="businessWizardSteps"
            sx={{
              width: "100%",
              background: "transparent !important",
              borderRadius: 0,
              padding: "0 !important",
              margin: 0,
              minHeight: "unset",
              "& .MuiStep-root": {
                padding: 0,
                margin: 0,
                flex: "none",
              },
              "& .MuiStep-root:last-of-type": {
                marginLeft: "auto",
              },
              "& .MuiStepLabel-root": {
                padding: 0,
                margin: 0,
              },
              "& .MuiStepConnector-root": {
                flex: 1,
                marginLeft: "12px",
                marginRight: "12px",
                padding: 0,
              },
              "& .MuiStepConnector-line": {
                borderColor: `${themeColors?.primary || "#4C0055"}33`,
                borderTopWidth: 2,
              },
              "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line":
                {
                  borderColor: themeColors?.primary || "#4C0055",
                },
              "& .MuiStepLabel-iconContainer": {
                display: "flex !important",
                paddingRight: "8px",
                paddingLeft: 0,
              },
              "& .MuiStepLabel-iconContainer svg": {
                display: "block !important",
              },
              "& .MuiStepLabel-labelContainer": {
                padding: "0 !important",
                margin: 0,
                background: "transparent !important",
                borderRadius: 0,
              },
              "& .MuiStep-horizontal .Mui-active + .MuiStepLabel-labelContainer":
                {
                  background: "transparent !important",
                  borderRadius: 0,
                  padding: "0 !important",
                },
              "& .MuiStepLabel-label": {
                fontFamily: "Saira, sans-serif",
                fontWeight: 700,
                fontSize: "14px",
                color: themeColors?.primary || "#4C0055",
                letterSpacing: 0,
                opacity: 1,
                margin: 0,
                padding: 0,
                whiteSpace: "nowrap",
              },
              "& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed, & .MuiStepLabel-label.Mui-disabled":
                {
                  color: themeColors?.primary || "#4C0055",
                  fontWeight: 700,
                  opacity: 1,
                },
            }}
          >
            {CREATE_WIZARD_STEPS.map((label) => (
              <Step key={label}>
                <StepLabel
                  StepIconComponent={(props) => (
                    <WizardStepIcon
                      {...props}
                      activeColor={themeColors?.primary || "#4C0055"}
                    />
                  )}
                >
                  {t(label)}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      ) : null}
      {loading ? (
        <Loader />
      ) : isCreateWizard && wizardStep === 1 ? (
        <>
          <Grid item xs={12} className="pl-4">
            <h1
              className="flex font-Saira font-[700] text-[17px]"
              style={{ color: themeColors?.primary }}
            >
              {t("add") + " " + t("station") + " " + t("Details")}
            </h1>
          </Grid>
          <StationModal
            embedded
            embeddedBusinessId={savedBusinessId}
            onEmbeddedComplete={() => {
              navigate("/businesses");
            }}
            onEmbeddedBack={() => setWizardStep(0)}
          />
        </>
      ) : (
        // isCreateWizard &&
        //   wizardStep === 2 &&
        //   savedStation &&
        //   savedBusinessId ? (
        //   <StationSubscriptionPlanStep
        //     stationId={savedStation.id}
        //     stationName={savedStation.name}
        //     businessId={
        //       Number(savedStation.businessId) || Number(savedBusinessId)
        //     }
        //     currency={watch("currency")?.value || watch("currency")?.label}
        //     themeColors={themeColors}
        //     onAssigned={() => navigate("/businesses")}
        //     onBack={() => setWizardStep(1)}
        //   />
        // ) :
        <SimpleModal
          // padding={false}
          visable={true}
          title={params?.id ? "editBusiness" : "addBusiness"}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={mainButtonText}
          showSecondaryButton={Boolean(
            showCreateWizardButtons || isBrandingEdit,
          )}
          secondaryButtonText={secondaryButtonText}
          secondaryButtonAction={secondaryButtonAction}
          secondaryButtonDisabled={!IsBtnEnabled}
          secondaryButtonType={
            showCreateWizardButtons ? "outlineSecondary" : undefined
          }
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={activeFormSubmit}
          formSubmit={activeFormSubmit}
          content={
            <Box
              className={`${
                (loggedInUserData.type === -1 ||
                  loggedInUserData.type === 100) &&
                params?.id
                  ? "flex w-full"
                  : "unset"
              }`}
            >
              {(loggedInUserData.type === -1 ||
                loggedInUserData.type === 100) &&
              params?.id ? (
                <VerticalTabs
                  tabs={tabs}
                  selectedIndex={currentTab}
                  handleChangeTabs={handleChangeTabs}
                  disableTabNo={watch("whiteLabel") ? [] : [1, 2]}
                />
              ) : null}
              <Box
                className={
                  (loggedInUserData.type === -1 ||
                    loggedInUserData.type === 100) &&
                  params?.id
                    ? "flex-1 min-w-0"
                    : undefined
                }
              >
                <Grid
                  container
                  component="form"
                  id="form"
                  spacing={2}
                  onSubmit={handleSubmitAuto}
                >
                  {currentTab === 0 ? (
                    <>
                      <Grid item xs={12} className="pl-4">
                        <h1
                          className="flex font-Saira font-[700] text-[17px]"
                          style={{ color: themeColors?.primary }}
                        >
                          {t("add") + " " + t("business") + " " + t("Details")}
                        </h1>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("title", {
                            validate: {
                              required: (value) =>
                                value?.trim() !== "" ||
                                t("This field is required."),
                            },
                            onChange: () => enableUpdateBtn(),
                          })}
                          label={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                          placeholder={localizedBusinessTitlePlaceholder(
                            primaryLanguage,
                          )}
                          title={`${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                            .replace(/\s+/g, " ")
                            .trim()}
                          fullWidth
                          error={!!errors.title}
                          helperText={errors.title?.message as string}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("secondaryTitle", {
                            onChange: () => enableUpdateBtn(),
                          })}
                          label={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                          placeholder={localizedBusinessTitlePlaceholder(
                            secondaryLanguage,
                          )}
                          title={`${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                            .replace(/\s+/g, " ")
                            .trim()}
                          fullWidth
                          error={!!errors.secondaryTitle}
                          helperText={errors.secondaryTitle?.message as string}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="primaryLanguage"
                          control={control}
                          defaultValue={null}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("primaryLanguage")}
                              {...field}
                              options={languageOptions}
                              isClearable
                              isSearchable
                              defaultPlaceholder="phSelectPrimaryLanguage"
                              onChange={(selectedOption: any) => {
                                field.onChange(selectedOption);
                                enableUpdateBtn();
                              }}
                              error={errors.primaryLanguage?.message as string}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="secondaryLanguage"
                          control={control}
                          defaultValue={null}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("secondaryLanguage")}
                              {...field}
                              options={languageOptions}
                              isClearable
                              isSearchable
                              defaultPlaceholder="phSelectSecondaryLanguage"
                              onChange={(selectedOption: any) => {
                                field.onChange(selectedOption);
                                enableUpdateBtn();
                              }}
                              error={
                                errors.secondaryLanguage?.message as string
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="currency"
                          control={control}
                          defaultValue={null}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("currency")}
                              {...field}
                              options={currencyOptions}
                              isClearable
                              isSearchable
                              defaultPlaceholder="phSelectCurrency"
                              onChange={(selectedOption: any) => {
                                field.onChange(selectedOption);
                                enableUpdateBtn();
                              }}
                              error={errors.currency?.message as string}
                            />
                          )}
                        />
                      </Grid>
                      {/* Extra business fields hidden — only title, languages, currency shown above
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="scrapItemGroupId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("scrapItemGroup") + " *"}
                              {...field}
                              options={scrapItemGroups}
                              isClearable
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={errors.scrapItemGroupId?.message as string}
                              isLoading={searchLoading === "scrapItemGroups"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "scrapItemGroups",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "scrapItemGroups",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(scrapItemGroupsData?.pages[0]?.data as any)
                                    .last
                                )
                                  handleDropdownActions(
                                    "scrapItemGroups",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="priceListId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("defaultPriceList") + " *"}
                              {...field}
                              options={priceLists}
                              isClearable
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={errors.priceListId?.message as string}
                              isLoading={searchLoading === "priceLists"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "priceLists",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "priceLists",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(priceListData?.pages[0]?.data as any).last
                                )
                                  handleDropdownActions(
                                    "priceLists",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="taxProcedureId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("taxProcedureId")}
                              {...field}
                              options={taxProcedures}
                              isClearable
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={errors.taxProcedureId?.message as string}
                              isLoading={searchLoading === "taxProcedures"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "taxProcedures",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "taxProcedures",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(taxProceduresData?.pages[0]?.data as any)
                                    .last
                                )
                                  handleDropdownActions(
                                    "taxProcedures",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="vendorId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("vendors") + " *"}
                              {...field}
                              options={supplierOptions}
                              isClearable
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={errors.vendorId?.message as string}
                              isLoading={searchLoading === "suppliers"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "suppliers",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "suppliers",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (!(vendorsData?.pages[0]?.data as any).last)
                                  handleDropdownActions(
                                    "suppliers",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="bankAccountId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: t("This field is required.") }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("ACCOUNT") + " *"}
                              {...field}
                              options={bankAccounts}
                              isClearable
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={errors.bankAccountId?.message as string}
                              isLoading={searchLoading === "accounts"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "accounts",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "accounts",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (!(vendorsData?.pages[0]?.data as any).last)
                                  handleDropdownActions("accounts", "loadNext");
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="sellBelowStock"
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <CommonCheckbox
                              {...field}
                              label={t("Sell Below Stock")}
                              onChange={(
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => field.onChange(event.target.checked)}
                              checked={field.value}
                              color={themeColors?.primary}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="number"
                          {...register("ownerCapital", {
                            required: t("This field is required."),
                            validate: (value) =>
                              value > 0 || t("amountGreaterThanZero"),
                          })}
                          label={t("ownerCapital") + " *"}
                          placeholder={t("ownerCapital")}
                          title={t("ownerCapital")}
                          fullWidth
                          error={!!errors.ownerCapital}
                          helperText={errors.ownerCapital?.message as string}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("cashContractNo")}
                          label={t("cashContractNo")}
                          placeholder={t("phCashContractNo")}
                          title={t("cashContractNo")}
                          fullWidth
                          error={!!errors.cashContractNo}
                          helperText={
                            errors.cashContractNo?.message as string
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="planId"
                          control={control}
                          defaultValue={null}
                          rules={{ required: false }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("subscriptionPlans")}
                              {...field}
                              options={subscriptionPlans}
                              isClearable
                              isSearchable
                              onChange={(selectedOption: any) =>
                                field.onChange(selectedOption)
                              }
                              error={errors.planId?.message as string}
                              isLoading={searchLoading === "subscriptionPlans"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "subscriptionPlans",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "subscriptionPlans",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (
                                  !(
                                    subscriptionPlansData?.pages[0]?.data as any
                                  ).last
                                )
                                  handleDropdownActions(
                                    "subscriptionPlans",
                                    "loadNext",
                                  );
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid> */}
                      {loggedInUserData.type === -1 ? (
                        <>
                          <Grid item xs={12} md={3}>
                            <TextField
                              {...register("cashContractNo")}
                              label={t("cashContractNo")}
                              placeholder={t("phCashContractNo")}
                              title={t("cashContractNo")}
                              fullWidth
                              error={!!errors.cashContractNo}
                              helperText={
                                errors.cashContractNo?.message as string
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <Controller
                              name="whiteLabel"
                              control={control}
                              defaultValue={false}
                              render={({ field }) => (
                                <CommonCheckbox
                                  {...field}
                                  label={t("branding")}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    field.onChange(event.target.checked);
                                    enableUpdateBtn();
                                  }}
                                  checked={!!field.value}
                                  color={themeColors?.primary}
                                />
                              )}
                            />
                          </Grid>
                        </>
                      ) : null}

                      <Grid item xs={12}>
                        <h1
                          className="flex font-Saira font-[700] text-[17px]"
                          style={{ color: themeColors?.primary }}
                        >
                          {t("userDetails")}
                        </h1>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("name", {
                            validate: {
                              required: (value) =>
                                value?.trim() !== "" ||
                                t("This field is required."),
                            },
                            onChange: () => enableUpdateBtn(),
                          })}
                          label={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`}
                          placeholder={localizedUserNamePlaceholder(
                            primaryLanguage,
                          )}
                          title={`${!primaryLanguage ? t("primary") : ""} ${t("name")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""} *`
                            .replace(/\s+/g, " ")
                            .trim()}
                          fullWidth
                          error={!!errors.name}
                          helperText={errors.name?.message as string}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("secondaryName", {
                            onChange: () => enableUpdateBtn(),
                          })}
                          label={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`}
                          placeholder={localizedUserNamePlaceholder(
                            secondaryLanguage,
                          )}
                          title={`${!secondaryLanguage ? t("secondary") : ""} ${t("name")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`
                            .replace(/\s+/g, " ")
                            .trim()}
                          fullWidth
                          error={!!errors.secondaryName}
                          helperText={errors.secondaryName?.message as string}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="email"
                          {...register("username", {
                            required: t("This field is required."),
                            pattern: {
                              value:
                                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: t("validEmail"),
                            },
                            onChange: () => enableUpdateBtn(),
                          })}
                          label={t("email") + " *"}
                          placeholder={t("phBusinessEmail")}
                          title={t("email")}
                          fullWidth
                          error={!!errors.username}
                          helperText={errors.username?.message as string}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      {!params?.id && !savedBusinessId ? (
                        <>
                          <Grid item xs={12} md={3}>
                            <TextField
                              type={watch("showPassword") ? "text" : "password"}
                              {...register("password", {
                                required: t("This field is required."),
                                validate: {
                                  passwordLength: (value) =>
                                    value.length >= 8 || t("minPasswordLength"),
                                },
                              })}
                              label={t("password") + " *"}
                              placeholder={t("phBusinessPassword")}
                              title={t("password")}
                              fullWidth
                              error={!!errors.password}
                              helperText={errors.password?.message as string}
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                endAdornment: watch("password") ? (
                                  <InputAdornment
                                    position="end"
                                    className="cursor-pointer"
                                  >
                                    {watch("showPassword") ? (
                                      <VisibilityIcon
                                        style={{ color: themeColors?.primary }}
                                        onClick={() =>
                                          setValue("showPassword", false)
                                        }
                                      />
                                    ) : (
                                      <VisibilityOffIcon
                                        style={{ color: themeColors?.primary }}
                                        onClick={() =>
                                          setValue("showPassword", true)
                                        }
                                      />
                                    )}
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <TextField
                              type={
                                watch("showConfirmPassword")
                                  ? "text"
                                  : "password"
                              }
                              {...register("confirmPassword", {
                                required: t("This field is required."),
                                validate: {
                                  passwordLength: (value) =>
                                    value === watch("password") ||
                                    t("confirmPasswordNewPassword"),
                                },
                              })}
                              label={t("confirmNewPassword") + " *"}
                              placeholder={t("phConfirmPassword")}
                              title={t("confirmNewPassword")}
                              fullWidth
                              error={!!errors.confirmPassword}
                              helperText={
                                errors.confirmPassword?.message as string
                              }
                              InputLabelProps={{ shrink: true }}
                              InputProps={{
                                endAdornment: watch("confirmPassword") ? (
                                  <InputAdornment
                                    position="end"
                                    className="cursor-pointer"
                                  >
                                    {watch("showConfirmPassword") ? (
                                      <VisibilityIcon
                                        style={{ color: themeColors?.primary }}
                                        onClick={() =>
                                          setValue("showConfirmPassword", false)
                                        }
                                      />
                                    ) : (
                                      <VisibilityOffIcon
                                        style={{ color: themeColors?.primary }}
                                        onClick={() =>
                                          setValue("showConfirmPassword", true)
                                        }
                                      />
                                    )}
                                  </InputAdornment>
                                ) : null,
                              }}
                            />
                          </Grid>
                        </>
                      ) : null}
                      <Grid item xs={12} md={3}>
                        <TextField
                          type="text"
                          {...register("phone", {
                            validate: {
                              phoneLength: (value) =>
                                !value ||
                                (value.length >= 5 && value.length <= 17) ||
                                t("phoneLength"),
                            },
                            onChange: (e) => {
                              const cleaned = String(
                                e?.target?.value || "",
                              ).replace(/[^0-9+]/g, "");
                              if (e?.target) e.target.value = cleaned;
                              enableUpdateBtn();
                            },
                          })}
                          label={t(phoneLabel)}
                          placeholder={t("phBusinessPhone")}
                          title={t(phoneLabel)}
                          fullWidth
                          error={!!errors.phone}
                          helperText={errors.phone?.message as any}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("address", {
                            onChange: () => enableUpdateBtn(),
                          })}
                          label={t("address")}
                          placeholder={t("phBusinessAddress")}
                          title={t("address")}
                          fullWidth
                          error={!!errors.address}
                          helperText={errors.address?.message as string}
                          InputLabelProps={{ shrink: true }}
                          multiline
                          rows={1}
                          sx={{
                            "& .MuiInputBase-fullWidth.MuiInputBase-multiline":
                              {
                                width: "100%",
                                paddingRight: "0px",
                                paddingBottom: 0,
                              },
                            "& .MuiInputBase-inputMultiline": {
                              resize: "vertical",
                              minHeight: "29px",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("taxNumber", {
                            onChange: () => enableUpdateBtn(),
                          })}
                          label={t("taxNumber")}
                          placeholder={t("phBusinessTaxNumber")}
                          title={t("taxNumber")}
                          fullWidth
                          error={!!errors.taxNumber}
                          helperText={errors.taxNumber?.message as string}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      {!params?.id && !savedBusinessId ? (
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="customRole"
                            control={control}
                            defaultValue={false}
                            render={({ field }) => (
                              <div>
                                <CommonCheckbox
                                  {...field}
                                  label={t("useCustomRole")}
                                  onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    field.onChange(event.target.checked);
                                    if (!event.target.checked)
                                      setValue("roleId", null);
                                  }}
                                  checked={field.value}
                                  color={themeColors?.primary}
                                />
                              </div>
                            )}
                          />
                        </Grid>
                      ) : null}
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="roleId"
                          control={control}
                          defaultValue={null}
                          rules={{
                            required: params?.id
                              ? t("This field is required.")
                              : watch("customRole")
                                ? t("This field is required.")
                                : false,
                          }}
                          render={({ field }) => (
                            <DynamicSelect
                              label={t("role") + " *"}
                              {...field}
                              options={roles}
                              isSearchable
                              isDisabled={
                                params?.id ? false : !watch("customRole")
                              }
                              defaultPlaceholder="phSelectRole"
                              onChange={(selectedOption: any) => {
                                field.onChange(selectedOption);
                                enableUpdateBtn();
                              }}
                              error={errors.roleId?.message as string}
                              isLoading={searchLoading === "roles"}
                              onInputChange={(
                                search: string,
                                { action }: { action: string },
                              ) => {
                                if (
                                  action === "input-change" ||
                                  (action !== "set-value" &&
                                    action !== "menu-close" &&
                                    action !== "input-blur")
                                )
                                  handleDropdownActions(
                                    "roles",
                                    "searchIt",
                                    search,
                                  );
                                else if (
                                  action === "input-blur" ||
                                  action === "menu-close"
                                ) {
                                  if (dropdownSearchTerm || pageNo > 1)
                                    handleDropdownActions(
                                      "roles",
                                      "searchIt",
                                      "",
                                    );
                                  else setDropdownSearchTerm("");
                                  setPageNo(1);
                                }
                              }}
                              onMenuScrollToBottom={() => {
                                if (!(rolesData?.pages[0]?.data as any).last)
                                  handleDropdownActions("roles", "loadNext");
                              }}
                              page={pageNo}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  ) : null}
                  {loggedInUserData.type === -1 &&
                  currentTab === 1 &&
                  params?.id ? (
                    <Grid
                      item
                      container
                      xs={12}
                      rowSpacing={2}
                      columnSpacing={2}
                    >
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.serviceTitle")}
                          label={t("adminTitle")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phAdminTitle")}
                          title={t("adminTitle")}
                          fullWidth
                          error={!!(errors.attributes as any)?.serviceTitle}
                          helperText={
                            (errors.attributes as any)?.serviceTitle
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.hostTitle")}
                          label={t("posTitle")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phPosTitle")}
                          title={t("posTitle")}
                          fullWidth
                          error={!!(errors.attributes as any)?.hostTitle}
                          helperText={
                            (errors.attributes as any)?.hostTitle
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.customerTitle")}
                          label={t("customerTitle")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phCustomerPortalTitle")}
                          title={t("customerTitle")}
                          fullWidth
                          error={!!(errors.attributes as any)?.customerTitle}
                          helperText={
                            (errors.attributes as any)?.customerTitle
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.footerText")}
                          label={t("footerText")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phFooterText")}
                          title={t("footerText")}
                          fullWidth
                          error={!!(errors.attributes as any)?.footerText}
                          helperText={
                            (errors.attributes as any)?.footerText
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.about")}
                          label={t("aboutUsLink")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phAboutUsLink")}
                          title={t("aboutUsLink")}
                          fullWidth
                          error={!!(errors.attributes as any)?.about}
                          helperText={
                            (errors.attributes as any)?.about?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.privacyPolicyLink")}
                          label={t("privacyPolicyLink")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phPrivacyPolicyLink")}
                          title={t("privacyPolicyLink")}
                          fullWidth
                          error={
                            !!(errors.attributes as any)?.privacyPolicyLink
                          }
                          helperText={
                            (errors.attributes as any)?.privacyPolicyLink
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.termsLink")}
                          label={t("termsLink")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phTermsLink")}
                          title={t("termsLink")}
                          fullWidth
                          error={!!(errors.attributes as any)?.termsLink}
                          helperText={
                            (errors.attributes as any)?.termsLink
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.hostUrl")}
                          label={t("posUrl")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phPosUrl")}
                          title={t("posUrl")}
                          fullWidth
                          error={!!(errors.attributes as any)?.hostUrl}
                          helperText={
                            (errors.attributes as any)?.hostUrl
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.adminUrl")}
                          label={t("adminUrl")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phAdminUrl")}
                          title={t("adminUrl")}
                          fullWidth
                          error={!!(errors.attributes as any)?.adminUrl}
                          helperText={
                            (errors.attributes as any)?.adminUrl
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.customerUrl")}
                          label={t("customerUrl")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phCustomerUrl")}
                          title={t("customerUrl")}
                          fullWidth
                          error={!!(errors.attributes as any)?.customerUrl}
                          helperText={
                            (errors.attributes as any)?.customerUrl
                              ?.message as string
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          {...register("attributes.serverIp")}
                          label={t("serverIp")}
                          InputLabelProps={{ shrink: true }}
                          placeholder={t("phServerIp")}
                          title={t("serverIp")}
                          fullWidth
                          error={!!(errors.attributes as any)?.serverIp}
                          helperText={
                            (errors.attributes as any)?.serverIp
                              ?.message as string
                          }
                        />
                      </Grid>
                    </Grid>
                  ) : null}
                  {loggedInUserData.type === -1 &&
                  currentTab === 2 &&
                  params?.id ? (
                    <>
                      <Grid item container xs={12} spacing={2}>
                        <Grid item xs={12} md={3}>
                          <Controller
                            name="attributes.themeId"
                            control={control}
                            defaultValue={null}
                            rules={{ required: t("This field is required.") }}
                            render={({ field }) => (
                              <DynamicSelect
                                label={t("theme") + " *"}
                                {...field}
                                options={(Object?.values(themes) || [])?.map(
                                  (theme: any) => ({
                                    value: theme.id,
                                    label: t(theme.label),
                                    ...theme,
                                  }),
                                )}
                                isSearchable
                                onChange={(selectedOption: any) => {
                                  field.onChange(selectedOption);
                                  if (selectedOption.value === 100)
                                    setValue(
                                      "attributes.customTheme",
                                      themes["100"],
                                    );
                                  else setValue("attributes.customTheme", null);
                                }}
                                error={
                                  (errors.attributes as any)?.themeId
                                    ?.message as string
                                }
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                      {Object.entries(watch("attributes.themeId") || {})
                        .slice(
                          Object.keys(
                            watch("attributes.themeId") || {},
                          ).indexOf("primary"),
                        )
                        .filter(([key]) => key !== "value")
                        .map(([key, value]: any) => {
                          const isDarkColor = (color: string) => {
                            if (
                              typeof color !== "string" ||
                              !color.startsWith("#")
                            )
                              return false;
                            if (
                              color === "#000" ||
                              color === "#000000" ||
                              color === "black"
                            )
                              return "#ffffff";
                            const [r, g, b] = color
                              .slice(1)
                              .match(/.{2}/g)!
                              .map((c) => parseInt(c, 16));
                            return (r * 299 + g * 587 + b * 114) / 1000 < 128;
                          };
                          return (
                            <Grid
                              key={key}
                              item
                              container
                              xs={12}
                              spacing={3}
                              className="flex gap-1 items-center"
                            >
                              <Grid
                                item
                                xs={12}
                                md={3}
                                className="flex items-center gap-2 justify-between"
                              >
                                <p className="font-Saira text-[#000] font-semibold mb-0 text-start">
                                  {t(key) + " " + t("color")}
                                </p>
                                {watch("attributes.themeId").value === 100 ? (
                                  <TextField
                                    {...register(
                                      `attributes.customTheme.${key}`,
                                    )}
                                    type="color"
                                    value={
                                      watch(`attributes.customTheme.${key}`) ||
                                      value
                                    }
                                    sx={{
                                      "& input": {
                                        padding: "0 !important",
                                        background:
                                          watch(
                                            `attributes.customTheme.${key}`,
                                          ) || value,
                                        width: "38px",
                                        height: "38px",
                                      },
                                      "& .MuiInputBase-formControl": {
                                        border: "0 !important",
                                        borderRadius: "0 !important",
                                        WebkitBoxShadow: "none !important",
                                      },
                                    }}
                                  />
                                ) : (
                                  <div
                                    className={`w-[40px] h-[40px] border border-black`}
                                    style={{ background: value }}
                                  ></div>
                                )}
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                md={6}
                                className="rounded-[8px] p-4 text-start ms-8 mt-5"
                                style={{
                                  background:
                                    watch(`attributes.customTheme.${key}`) ||
                                    value,
                                }}
                              >
                                <p
                                  className="font-Saira font-semibold mb-0"
                                  style={{
                                    color: isDarkColor(
                                      watch(`attributes.customTheme.${key}`) ||
                                        value,
                                    )
                                      ? "#fff"
                                      : "#000",
                                  }}
                                >
                                  {t("themeTextColor")}
                                </p>
                              </Grid>
                              <Grid
                                item
                                xs={12}
                                md={3}
                                className="flex items-center gap-2"
                              >
                                {/* <p className="font-Saira text-[#3A3A3A] font-medium mb-0">{t('textColor')}</p>
                                                <div className={`w-[40px] h-[40px] border border-black`} style={{ background: isDarkColor(value) ? '#fff' : '#000' }}></div> */}
                              </Grid>
                            </Grid>
                          );
                        })}
                    </>
                  ) : null}
                </Grid>
                {loggedInUserData.type === -1 &&
                currentTab === 1 &&
                params?.id ? (
                  <Grid item container xs={12} spacing={2} className="mt-[2px]">
                    <Grid item xs={12} lg={4}>
                      <Typography className="font-Saira font-[600] text-[13px] text-txt-color text-start">
                        {t("logo")}
                      </Typography>
                      <BusinessMedia
                        formData={getValues()}
                        entity="logo"
                        callback={async (url: string) => {
                          setValue("attributes.logo", url);
                          const preview = await fetchMediaAsDataUrl(url || "");
                          setMediaPreviews((prev) => ({
                            ...prev,
                            logo: preview,
                          }));
                        }}
                        setIsBtnEnabled={setIsBtnEnabled}
                      />
                      <MediaImage src={mediaPreviews.logo} />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Typography className="font-Saira font-[600] text-[13px] text-txt-color text-start">
                        {t("logInPageImage")}
                      </Typography>
                      <BusinessMedia
                        formData={getValues()}
                        entity="logInPageImage"
                        callback={async (url: string) => {
                          setValue("attributes.logInPageImage", url);
                          const preview = await fetchMediaAsDataUrl(url || "");
                          setMediaPreviews((prev) => ({
                            ...prev,
                            logInPageImage: preview,
                          }));
                        }}
                        setIsBtnEnabled={setIsBtnEnabled}
                      />
                      <MediaImage src={mediaPreviews.logInPageImage} />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                      <Typography className="font-Saira font-[600] text-[13px] text-txt-color text-start">
                        {t("favicon")}
                      </Typography>
                      <BusinessMedia
                        formData={getValues()}
                        entity="favIcon"
                        callback={async (url: string) => {
                          setValue("attributes.favIcon", url);
                          const preview = await fetchMediaAsDataUrl(url || "");
                          setMediaPreviews((prev) => ({
                            ...prev,
                            favIcon: preview,
                          }));
                        }}
                        setIsBtnEnabled={setIsBtnEnabled}
                      />
                      <MediaImage src={mediaPreviews.favIcon} />
                    </Grid>
                  </Grid>
                ) : null}
                {currentTab > 0 ? (
                  <Grid item xs={12} md={6} className="text-start">
                    <Button
                      variant="contained"
                      className={`${isScreenSmallOrGreater ? "absolute" : ""} font-Saira font-semibold leading-[26px] bg-white text-black mt-[26px] px-[20px]`}
                      onClick={() => handleChangeTabs(currentTab - 1)}
                    >
                      {t("Previous")}
                    </Button>
                  </Grid>
                ) : null}
              </Box>
            </Box>
          }
        />
      )}
    </Box>
  );
}
